// src/App.js
/*import React, { useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import "./App.css";
import keycloak from "./APIs/keycloak";
import AppRoutes from "./AppRoutes";
import { fetchCurrentUser } from "./APIs/authApi";
import { resolveRouteForCurrentUser } from "./APIs/utils/authRouting";
import { claimProviderByNpi } from "./APIs/providerApi";
import { UserProvider } from "./APIs/UserContext";

let keycloakAlreadyInitialized = false;

function App() {
  const [patientId, setPatientId] = useState(null);
  const [providerId, setProviderId] = useState(null);
  const [kcInitialized, setKcInitialized] = useState(false);
  const [kcError, setKcError] = useState(null);

  const PENDING_CLAIM_KEY = "medmaze_pending_claim_npi";

  function getPendingClaimNpi() {
    try {
      const v = localStorage.getItem(PENDING_CLAIM_KEY);
      return v ? String(v).replace(/\D/g, "") : null;
    } catch {
      return null;
    }
  }

  function clearPendingClaimNpi() {
    try {
      localStorage.removeItem(PENDING_CLAIM_KEY);
    } catch {}
  }

  useEffect(() => {
    async function initKeycloak() {
      if (keycloakAlreadyInitialized) {
        setKcInitialized(true);
        return;
      }

      keycloakAlreadyInitialized = true;

      try {
        await keycloak.init({
          onLoad: "check-sso",
          pkceMethod: "S256",
        });

        // ✅ Always attempt /me after init
        try {
          const me = await fetchCurrentUser();

          if (me?.userType === "PATIENT" && me.patientId != null) {
            setPatientId(Number(me.patientId));
          }

          if (me?.userType === "PROVIDER" && me.providerId != null) {
            setProviderId(Number(me.providerId));
          }

          // ✅ Finalize provider claim if we have a pending NPI and we're authenticated
          if (keycloak.authenticated) {
            const pendingNpi = getPendingClaimNpi();

            // Only attempt claim when we have pending NPI AND the user is a PROVIDER
            if (pendingNpi && me?.userType === "PROVIDER") {
              try {
                const emailFromToken = keycloak?.tokenParsed?.email || null;
                if (!emailFromToken) {
                  throw new Error("Missing email in token.");
                }

                await claimProviderByNpi(pendingNpi, { email: emailFromToken });

                clearPendingClaimNpi();
                window.location.replace("/ProviderProfile?justClaimed=1");
                return;
              } catch (err) {
                clearPendingClaimNpi();

                const status = err?.response?.status;
                console.error(
                  "Provider claim failed:",
                  status,
                  err?.response?.data || err,
                );
              }
            }

            // ✅ Normal routing after claim attempt (or if no claim pending)
            if (me) {
              const route = resolveRouteForCurrentUser(me);
                "🔀 Resolved route:",
                route,
                "Current path:",
                window.location.pathname,
              );

              if (route && route !== window.location.pathname) {
                window.location.replace(route);
                return;
              }
            }
          }
        } catch (err) {
        }

        setKcInitialized(true);
      } catch (err) {
        console.error("Keycloak initialization failed:", err);
        setKcError("Unable to connect to login service.");
        setKcInitialized(true);
      }
    }

    initKeycloak();
  }, []);

  if (!kcInitialized && !kcError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
          <p className="text-gray-700">Initializing authentication...</p>
        </div>
      </div>
    );
  }

  if (kcError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
          <p className="text-red-600 mb-4">{kcError}</p>
          <p className="text-gray-700 text-sm">
            You can still try to access the app, but login might not work
            correctly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <UserProvider>
        <AppRoutes patientId={patientId} providerId={providerId} />
      </UserProvider>
    </Router>
  );
}

export default App;*/

import React, { useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import "./App.css";
import keycloak from "./APIs/keycloak";
import AppRoutes from "./AppRoutes";
import { claimProviderByNpi } from "./APIs/providerApi";
import { UserProvider } from "./APIs/UserContext";
import AuthenticatedRouter from "./APIs/authenticatedRouter";
import Toast from "./modals/Toast";
import { setToastCallback } from "./APIs/utils/toastManager";
import { BackendReadyProvider } from "./APIs/BackendReadyContext";

// Stores the keycloak.init() promise so React Strict Mode's double-mount
// can await the same init instead of skipping it on the second mount.
let initPromise = null;

function App() {
  const [kcInitialized, setKcInitialized] = useState(false);
  const [kcError, setKcError] = useState(null);

  const [toast, setToast] = useState(null);

  const PENDING_CLAIM_KEY = "medmaze_pending_claim_npi";

  useEffect(() => {
    setToastCallback((message, type) => {
      setToast({ message, type });
    });
  }, []);

  function getPendingClaimNpi() {
    try {
      const v = localStorage.getItem(PENDING_CLAIM_KEY);
      return v ? String(v).replace(/\D/g, "") : null;
    } catch {
      return null;
    }
  }

  function clearPendingClaimNpi() {
    try {
      localStorage.removeItem(PENDING_CLAIM_KEY);
    } catch {}
  }

  useEffect(() => {
    let mounted = true;
    let refreshInterval = null;

    async function initKeycloak() {
      // Reuse the same init promise across Strict Mode double-mount.
      // Both mounts await the same promise, so the app never renders
      // until keycloak.init() has fully completed (including code exchange).
      if (!initPromise) {
        // Diagnostic: capture URL state when init runs

        initPromise = keycloak.init({
          onLoad: "check-sso",
          pkceMethod: "S256",
          checkLoginIframe: false,
          enableLogging: true,
          responseMode: "fragment",
          redirectUri: window.location.origin + window.location.pathname,
        });
      }

      try {
        await initPromise;

        // If this mount was cleaned up by Strict Mode, skip side effects
        if (!mounted) return;


        // Auto-refresh token every 60 seconds
        refreshInterval = setInterval(() => {
          keycloak
            .updateToken(70)
            .then((refreshed) => {
              if (refreshed) console.log("Token was refreshed");
            })
            .catch(() => {
              console.error("Failed to refresh token");
            });
        }, 60000);

        // Handle provider claim flow (if pending)
        if (keycloak.authenticated) {
          const pendingNpi = getPendingClaimNpi();

          if (pendingNpi) {
            try {
              const emailFromToken = keycloak?.tokenParsed?.email || null;
              if (!emailFromToken) {
                throw new Error("Missing email in token.");
              }

              await new Promise((resolve) => setTimeout(resolve, 500));

              await claimProviderByNpi(pendingNpi, { email: emailFromToken });

              clearPendingClaimNpi();
              console.log(
                "Provider claim successful, UserContext will redirect",
              );
            } catch (err) {
              clearPendingClaimNpi();
              console.error(
                "Provider claim failed:",
                err?.response?.status,
                err?.response?.data || err,
              );
            }
          }
        }

        if (mounted) setKcInitialized(true);
      } catch (err) {
        if (!mounted) return;
        console.error("Keycloak initialization failed:", err);
        setKcError("Unable to connect to login service.");
        setKcInitialized(true);
      }
    }

    initKeycloak();

    return () => {
      mounted = false;
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, []);

  if (!kcInitialized && !kcError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
          <p className="text-gray-700">Initializing authentication...</p>
        </div>
      </div>
    );
  }

  if (kcError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
          <p className="text-red-600 mb-4">{kcError}</p>
          <p className="text-gray-700 text-sm">
            You can still try to access the app, but login might not work
            correctly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <BackendReadyProvider>
      <Router>
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        <UserProvider>
          <AuthenticatedRouter>
            <AppRoutes />
          </AuthenticatedRouter>
        </UserProvider>
      </Router>
    </BackendReadyProvider>
  );
}

export default App;
